import { type ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import type { ElementRef } from '@angular/core';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxIcon } from '../../directives';
import { QPX_POPOVER_HOVER_CONTEXT } from '../../services/popover/inject-popover-with-hover';
import { QpxMetadata, type QpxMetadataItem } from '../metadata';
import { createCardState } from './card-state';
import { createItemsState } from './items';
import { getHiddenItemIds, type Measurement } from './layout';

const DEFAULT_MAX_LINES = 1;
let nextPopupId = 0;
const INFO_CARD_POSITIONS: ConnectedPosition[] = [
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: 0,
        offsetY: 0.125 * 16,
    },
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: 0,
        offsetY: -0.125 * 16,
    },
];

@Component({
    selector: 'qpx-overflow-metadata',
    imports: [
        OverlayModule,
        MatButtonModule,
        NgIcon,
        QpxMetadata,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './overflow-metadata.html',
    styleUrl: './overflow-metadata.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'relative block min-w-0',
    },
})
export class QpxOverflowMetadata {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly parentHover = inject(QPX_POPOVER_HOVER_CONTEXT, {
        optional: true,
    });
    //#endregion

    //#region Inputs
    public readonly items = input<readonly QpxMetadataItem[]>([]);

    public readonly maxLines = input(DEFAULT_MAX_LINES);

    public readonly separator = input<string>();

    public readonly presentation = input<'inline' | 'detail'>('inline');

    public readonly infoAriaLabel = input('Show more metadata');

    public readonly cardAriaLabel = input('Additional metadata');

    //#endregion

    //#region Template references
    private readonly containerRef =
        viewChild.required<ElementRef<HTMLElement>>('container');

    private readonly measurementRowRef =
        viewChild.required<ElementRef<HTMLElement>>('measurementRow');

    private readonly triggerRef =
        viewChild<ElementRef<HTMLElement>>('triggerButton');

    private readonly cardRef = viewChild<ElementRef<HTMLElement>>('card');
    //#endregion

    //#region State
    protected readonly cardPositions = INFO_CARD_POSITIONS;
    protected readonly popupId = `qpx-overflow-metadata-popup-${nextPopupId++}`;

    private readonly hiddenItemIds = signal<readonly string[]>([]);
    private readonly itemsState = createItemsState(
        this.items,
        this.hiddenItemIds
    );
    private readonly cardState = createCardState(
        this.itemsState.hasHiddenItems
    );

    protected readonly visibleItems = this.itemsState.visibleItems;
    protected readonly hiddenItems = this.itemsState.hiddenItems;
    protected readonly hasHiddenItems = this.itemsState.hasHiddenItems;
    protected readonly cardOpen = this.cardState.cardOpen;

    private resizeFrameId: number | null = null;
    //#endregion

    constructor() {
        afterNextRender({
            read: () => {
                const container = this.containerRef()?.nativeElement;
                const measurementRow = this.measurementRowRef()?.nativeElement;

                if (!container || !measurementRow) {
                    return;
                }

                this.setupResizeObserver(container);
                this.setupMutationObserver(measurementRow);
                this.scheduleMeasurement();
            },
        });

        this.destroyRef.onDestroy(() => {
            if (
                this.resizeFrameId !== null &&
                typeof cancelAnimationFrame === 'function'
            ) {
                cancelAnimationFrame(this.resizeFrameId);
            }
        });
    }

    //#region Public methods
    public remeasure(): void {
        this.measureOverflow();
    }
    //#endregion

    //#region Template methods
    protected toggleCard(): void {
        this.cardState.togglePinned();
    }

    protected onTriggerMouseEnter(): void {
        this.cardState.setTriggerHovered(true);
    }

    protected onTriggerMouseLeave(event: MouseEvent): void {
        this.cardState.setTriggerHovered(false);

        if (this.isPointerMovingWithinCard(event)) {
            this.cardState.setCardHovered(true);
        }
    }

    protected onCardMouseEnter(): void {
        this.parentHover?.onMouseEnter();
        this.cardState.setCardHovered(true);
    }

    protected onCardMouseLeave(event: MouseEvent): void {
        this.parentHover?.onMouseLeave(event.relatedTarget);
        this.cardState.setCardHovered(false);

        if (this.isPointerMovingToTrigger(event)) {
            this.cardState.setTriggerHovered(true);
        }
    }

    protected onFocusIn(): void {
        this.cardState.setFocusWithin(true);
    }

    protected onFocusOut(event: FocusEvent): void {
        if (this.isFocusMovingWithinCurrentSurface(event)) {
            return;
        }

        this.cardState.setFocusWithin(false);
    }

    protected closeCard(): void {
        this.cardState.close();
    }
    //#endregion

    //#region Private methods
    private setupResizeObserver(element: HTMLElement): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            this.scheduleMeasurement();
        });

        observer.observe(element);
        this.destroyRef.onDestroy(() => {
            observer.disconnect();
        });
    }

    private setupMutationObserver(element: HTMLElement): void {
        if (typeof MutationObserver === 'undefined') {
            return;
        }

        const observer = new MutationObserver(() => {
            this.scheduleMeasurement();
        });

        observer.observe(element, {
            childList: true,
            characterData: true,
            subtree: true,
        });

        this.destroyRef.onDestroy(() => {
            observer.disconnect();
        });
    }

    private scheduleMeasurement(): void {
        if (this.resizeFrameId !== null) {
            return;
        }

        if (typeof requestAnimationFrame !== 'function') {
            this.measureOverflow();

            return;
        }

        this.resizeFrameId = requestAnimationFrame(() => {
            this.resizeFrameId = null;
            this.measureOverflow();
        });
    }

    private measureOverflow(): void {
        const items = this.items();

        if (items.length === 0) {
            this.hiddenItemIds.set([]);
            this.closeCard();

            return;
        }

        const containerWidth = this.containerRef().nativeElement.clientWidth;

        if (containerWidth <= 0) {
            this.hiddenItemIds.set([]);
            this.closeCard();

            return;
        }

        const measurementRow = this.measurementRowRef().nativeElement;
        const measurement = this.readMeasurement(
            measurementRow,
            containerWidth,
            this.maxLines()
        );
        const hiddenIds = getHiddenItemIds(items, measurement);

        this.hiddenItemIds.set(hiddenIds);

        if (hiddenIds.length === 0) {
            this.closeCard();
        }
    }

    private readMeasurement(
        measurementRow: HTMLElement,
        containerWidth: number,
        maxLines: number
    ): Measurement {
        const itemWidths = new Map<string, number>();

        for (const element of Array.from(
            measurementRow.querySelectorAll<HTMLElement>(
                '[data-measure-item-id]'
            )
        )) {
            const itemId = element.dataset['measureItemId'];

            if (!itemId) {
                continue;
            }

            itemWidths.set(itemId, Math.ceil(element.offsetWidth));
        }

        return {
            containerWidth,
            gap: this.readGap(measurementRow),
            itemWidths,
            infoTriggerWidth: Math.ceil(
                measurementRow.querySelector<HTMLElement>(
                    '[data-measure-info-trigger]'
                )?.offsetWidth ?? 0
            ),
            maxLines,
        };
    }

    private readGap(element: HTMLElement): number {
        const styles = getComputedStyle(element);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

        return Number.isFinite(gap) ? gap : 0;
    }

    private isPointerMovingWithinCard(event: MouseEvent): boolean {
        const relatedTarget = event.relatedTarget;

        return relatedTarget instanceof Node && this.isCardNode(relatedTarget);
    }

    private isPointerMovingToTrigger(event: MouseEvent): boolean {
        const relatedTarget = event.relatedTarget;

        return (
            relatedTarget instanceof Node && this.isTriggerNode(relatedTarget)
        );
    }

    private isFocusMovingWithinCurrentSurface(event: FocusEvent): boolean {
        const relatedTarget = event.relatedTarget;

        if (!(relatedTarget instanceof Node)) {
            return false;
        }

        return (
            this.isTriggerNode(relatedTarget) || this.isCardNode(relatedTarget)
        );
    }

    private isTriggerNode(node: Node): boolean {
        const trigger = this.triggerRef()?.nativeElement;

        return trigger instanceof HTMLElement && trigger.contains(node);
    }

    private isCardNode(node: Node): boolean {
        const card = this.cardRef()?.nativeElement;

        return card instanceof HTMLElement && card.contains(node);
    }
    //#endregion
}
